import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { FileSpreadsheet, Mail, FileText, ClipboardCheck, Plug, UserPlus, BadgeCheck, Clock, CheckCircle2, BanknoteIcon, ArrowRight } from "lucide-react";

/* ─── Count-up hook ─── */
const useCountUp = (end, decimals = 0, duration = 1400) => {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  useEffect(() => {
    if (!inView) return;
    let startTime = null;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(parseFloat((eased * end).toFixed(decimals)));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [inView, end, decimals, duration]);
  return { value, ref };
};

const oldWay = [
  { icon: FileSpreadsheet, label: "Spreadsheet" },
  { icon: Mail, label: "Emails" },
  { icon: FileText, label: "PDFs" },
  { icon: ClipboardCheck, label: "Manual approvals" },
  { icon: Plug, label: "Disconnected systems" },
];

const flow = [
  { icon: UserPlus, label: "Invite" },
  { icon: BadgeCheck, label: "Onboard" },
  { icon: Clock, label: "Track" },
  { icon: CheckCircle2, label: "Approve" },
  { icon: BanknoteIcon, label: "Pay" },
];

export default function ProductStory() {
  const { value: hours, ref: hoursRef } = useCountUp(3.2, 1, 1400);
  const panelRef = useRef(null);
  const panelVisible = useInView(panelRef, { once: true, margin: "-50px" });
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (!panelVisible) return;
    let interval;
    const timeout = setTimeout(() => {
      interval = setInterval(() => setActiveIdx(p => (p + 1) % flow.length), 1500);
    }, 800);
    return () => { clearTimeout(timeout); clearInterval(interval); };
  }, [panelVisible]);

  return (
    <section data-testid="product-story-section" className="relative py-16 sm:py-24 lg:py-36 border-t border-[#0E1A1A]">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">

          {/* Left: copy + stats */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="text-[11px] uppercase tracking-[0.22em] text-[#FF4F00] font-bold mb-4"
            >
              The story
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.65, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.035em] leading-[1.02] font-medium mb-6"
              data-testid="product-story-headline"
            >
              Not another <span className="italic font-normal text-[#9BB8B8]">HR</span> platform.
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="text-[#9BB8B8] text-lg leading-relaxed max-w-md"
            >
              nexus-vs was designed specifically for companies managing independent contractors. Less spreadsheets. Less manual work. More visibility.
            </motion.p>

            <div className="mt-8 sm:mt-12 grid grid-cols-2 gap-4 sm:gap-8 max-w-sm">
              <div>
                <div ref={hoursRef} className="font-display text-4xl tracking-tight">
                  {hours.toFixed(1)}h
                </div>
                <div className="text-xs text-[#7A9B9B] mt-1.5 leading-snug">Avg. weekly time saved per ops lead</div>
              </div>
              <div>
                <motion.div
                  initial={{ scale: 0.4, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3, type: "spring", bounce: 0.45 }}
                  className="font-display text-4xl tracking-tight"
                >
                  0
                </motion.div>
                <div className="text-xs text-[#7A9B9B] mt-1.5 leading-snug">Spreadsheets needed to run payroll</div>
              </div>
            </div>
          </div>

          {/* Right: Before → After */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_52px_1fr] items-start gap-4 lg:gap-0">

            {/* BEFORE panel */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-3 flex items-center gap-2">
                <span className="w-6 h-px bg-[#1F3535]" /> Before
              </div>
              <div className="rounded-2xl p-4 space-y-2 relative overflow-hidden border border-[#E8E4D8]" style={{ background: "#FFFFF0" }}>
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(239,68,68,0.06),transparent_70%)] pointer-events-none" />
                <div className="flex justify-end mb-1">
                  <span className="text-[9px] font-mono text-red-500 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">CHAOS</span>
                </div>
                {oldWay.map((o, i) => (
                  <motion.div
                    key={o.label}
                    initial={{ opacity: 0, x: -8 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: i * 0.06 }}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white border border-[#EAE5D5]"
                    data-testid={`old-step-${i}`}
                  >
                    <o.icon className="w-4 h-4 text-[#A1A1AA] shrink-0" />
                    <span className="text-xs text-[#52525B]">{o.label}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Arrow connector */}
            <div className="flex items-center justify-center lg:pt-[3.25rem]">
              <div className="rotate-90 lg:rotate-0">
                <motion.div
                  initial={{ opacity: 0, scale: 0.4 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.65, type: "spring", bounce: 0.5 }}
                  className="w-7 h-7 rounded-full bg-[#FF4F00] grid place-items-center shadow-[0_0_18px_rgba(255,79,0,0.45)]"
                >
                  <ArrowRight className="w-3.5 h-3.5 text-white" />
                </motion.div>
              </div>
            </div>

            {/* WITH NEXUS-VS panel */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF4F00] font-bold mb-3 flex items-center gap-2">
                <span className="w-6 h-px bg-[#FF4F00]" /> With nexus-vs
              </div>
              <div ref={panelRef} className="rounded-2xl p-4 space-y-2 relative overflow-hidden border border-[#D4EDE0]" style={{ background: "#F0FFF0" }}>
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,79,0,0.05),transparent_70%)] pointer-events-none" />
                <div className="flex justify-end mb-1">
                  <span className="text-[9px] font-mono text-[#059669] bg-[#ECFDF5] border border-[#A7F3D0] rounded-full px-2 py-0.5">CONNECTED</span>
                </div>
                {flow.map((f, i) => (
                  <div key={f.label} className="relative" data-testid={`flow-step-${i}`}>
                    {i === activeIdx && (
                      <motion.div
                        layoutId="flow-highlight"
                        className="absolute inset-0 rounded-lg bg-[#22C55E]/15 border border-[#22C55E]/40"
                        transition={{ type: "spring", stiffness: 380, damping: 28 }}
                      />
                    )}
                    <motion.div
                      initial={{ opacity: 0, x: 8 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.45, delay: i * 0.09 + 0.3 }}
                      className={`relative flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors duration-300 ${
                        i === activeIdx ? "border-transparent bg-transparent" : "bg-[#E2F0E8] border-[#B8D8C4]"
                      }`}
                    >
                      <div className={`w-6 h-6 grid place-items-center rounded-md border shrink-0 transition-colors duration-300 ${
                        i === activeIdx ? "bg-[#22C55E]/20 border-[#22C55E]/40" : "bg-[#FF4F00]/10 border-[#FF4F00]/25"
                      }`}>
                        <f.icon className={`w-3.5 h-3.5 transition-colors duration-300 ${
                          i === activeIdx ? "text-[#16A34A]" : "text-[#FF4F00]"
                        }`} />
                      </div>
                      <span className={`text-xs font-medium transition-colors duration-300 ${
                        i === activeIdx ? "text-[#15803D] font-semibold" : "text-[#0A0A0B]"
                      }`}>{f.label}</span>
                      <span className="ml-auto text-[9px] font-mono text-[#A1A1AA]">0{i + 1}</span>
                    </motion.div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
